import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Formik, Form } from "formik";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { FormInput } from "@/core/components/form-input";
import { Button, TimedButton } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { SERVICE_TITAN_STATUSES } from "@/business/constants";
import {
  useServiceTitanIntegration,
  ServiceTitanFormValues,
} from "@/business-settings/business/business-service-titan-settings/useServiceTitanIntegration";
import { formatValidationErrors } from "@/core/api/errors";
import { ApiError } from "@/core/api/types";

const STATUS_MESSAGES = {
  SUCCESS: "ServiceTitan integration updated successfully",
  PROCESSING: "Saved. We're processing your information.",
  CONNECTION_FAILED: "Connection failed. Please check your credentials.",
  UPDATE_FAILED:
    "Failed to update Service Titan integration. Please try again.",
} as const;

interface ServiceTitanIntegrationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string;
}

export const ServiceTitanIntegrationModal = ({
  isOpen,
  onOpenChange,
  businessId,
}: ServiceTitanIntegrationModalProps) => {
  const [lastSubmittedValues, setLastSubmittedValues] =
    useState<ServiceTitanFormValues | null>(null);

  const {
    status,
    config,
    initialValues,
    validationSchema,
    setHasConnectionError,
    isLoading,
    isPending,
    handleSubmit: handleSubmitCredentials,
    showTenantIdField,
    showClientFields,
    showErrorState,
    showSuccessState,
  } = useServiceTitanIntegration({
    businessId,
    showClientAndSecretKey: "modalShowClientAndSecret",
    hideTenantIdWhenEstablished: true,
    onSuccess: (resp) => {
      const nextStatus = resp.data?.credentials_status;
      const isComplete =
        nextStatus === SERVICE_TITAN_STATUSES.connection_established;
      const connectionFailed =
        nextStatus === SERVICE_TITAN_STATUSES.credentials_received;

      if (isComplete) {
        toast.success(STATUS_MESSAGES.SUCCESS);
        setHasConnectionError(false);
      } else if (connectionFailed) {
        setHasConnectionError(true);
        toast.error(STATUS_MESSAGES.CONNECTION_FAILED);
      } else {
        toast.success(STATUS_MESSAGES.PROCESSING);
        setHasConnectionError(false);
      }
    },
    onError: (error: Error) => {
      let description: string | undefined;

      if (error instanceof ApiError && error.details) {
        description = formatValidationErrors(error.details);
      }

      toast.error(STATUS_MESSAGES.UPDATE_FAILED, {
        description: description || "Please try again.",
      });
      setHasConnectionError(false);
    },
  });

  const handleClose = () => {
    if (!isPending) {
      setHasConnectionError(false);
      onOpenChange(false);
    }
  };

  const isFormDisabled = (values: ServiceTitanFormValues) =>
    !businessId ||
    isPending ||
    (lastSubmittedValues &&
      JSON.stringify(values) === JSON.stringify(lastSubmittedValues));

  const handleSubmit = (values: ServiceTitanFormValues) => {
    setLastSubmittedValues(values);
    handleSubmitCredentials(values);
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Loading Service Titan Integration...</DialogTitle>
          </DialogHeader>
          <Stack gap="lg" align="center" fullWidth className="pb-8">
            <Loader2 className="h-6 w-6 animate-spin text-secondary-text" />
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" hideCloseButton>
        <DialogHeader className="border border-l-0 border-r-0 border-t-0 border-stroke-tertiary">
          <DialogTitle>Service Titan Integration</DialogTitle>
        </DialogHeader>

        <Formik<ServiceTitanFormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount={true}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, isValid, getFieldProps }) => (
            <Form>
              <Stack gap="md" fullWidth className="p-6 pt-2">
                {showErrorState ? (
                  <Stack
                    gap="md"
                    align="start"
                    justify="center"
                    direction="row"
                    fullWidth
                    className="rounded-lg border border-stroke-negative bg-red-50 p-4"
                  >
                    <ExclamationTriangleIcon className="h-6 w-12 text-negative-text" />
                    <Typography variant="bodyW1">
                      We were unable to connect to your Service Titan, please
                      check that all values are correct and submit again.
                    </Typography>
                  </Stack>
                ) : (
                  !!config.modalSubtitle && (
                    <Stack
                      gap="md"
                      align="center"
                      justify="center"
                      direction="row"
                      fullWidth
                    >
                      {status !== SERVICE_TITAN_STATUSES.awaiting_tenant_id &&
                        !showSuccessState && (
                          <ClockIcon className="h-6 w-6 text-green-600" />
                        )}
                      <Typography
                        variant="bodyW1"
                        className="text-center text-secondary-text"
                      >
                        {config.modalSubtitle}
                      </Typography>
                    </Stack>
                  )
                )}

                <Stack gap="md" fullWidth>
                  {showTenantIdField && (
                    <FormInput
                      label="Tenant ID"
                      placeholder="Enter your Service Titan Tenant ID"
                      {...getFieldProps("tenant_id")}
                      fullWidth
                      disabled={isPending}
                      required
                    />
                  )}

                  {showClientFields && (
                    <>
                      <FormInput
                        label="Client ID"
                        placeholder="Enter your Service Titan Client ID"
                        {...getFieldProps("client_id")}
                        fullWidth
                        disabled={isPending}
                        required
                      />
                      <FormInput
                        label="Client Secret"
                        placeholder="Enter your Service Titan Client Secret"
                        type="password"
                        autoSave="off"
                        {...getFieldProps("client_secret")}
                        fullWidth
                        disabled={isPending}
                        required
                      />
                    </>
                  )}
                </Stack>

                {showSuccessState && (
                  <Stack align="center" fullWidth>
                    <CheckCircleIcon className="h-10 w-10 text-green-600" />
                  </Stack>
                )}

                {config.description && (
                  <Stack gap="sm" align="center" fullWidth>
                    <Typography
                      variant="bodyW1"
                      className="text-center text-secondary-text"
                    >
                      {config.description}
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <DialogFooter className="w-full">
                {config.modalShowSubmitButton ? (
                  <>
                    <Button
                      type="button"
                      variant="tertiary"
                      onClick={handleClose}
                      disabled={isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="min-w-[84px]"
                      disabled={!isValid || isFormDisabled(values) || false}
                      loading={isPending}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <Stack align="center" fullWidth>
                    <TimedButton
                      type="button"
                      onTimeout={handleClose}
                      onClick={handleClose}
                    >
                      Close
                    </TimedButton>
                  </Stack>
                )}
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
